import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./pages/layout/Navbar";
import Sidebar from "./pages/layout/Sidebar";
import Dashboard from "./pages/newDashboard";
import Team from "./pages/team";
import ManagePatient from "./pages/ManagePatient";
import Invoices from "./pages/Invoice";
import Contacts from "./pages/contacts";
import Form from "./pages/form";
import FAQ from "./pages/faq";
import { Box, CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import Calendar from "./pages/calendar/calendar";
import Inventory from "./pages/inventory";
import FrontDesk from "./pages/frontDesk";
import { useAuth } from "./context/AuthContext";
import LoginPage from "./pages/login";
import PathManagePatient from "./pages/PathManagePatient";
import { isPathModuleStaffRole } from "./utils/pathRoleUtils";
import AttendanceHistory from "./pages/attendance";
//import TokenDashboard from "./pages/PatientInQueue/TokenDashboard.jsx";
import TokenDashboard from "./pages/PatientInQueue/tokenIndex.jsx";
import WardsBeds from "./pages/wardsBeds";
import IpdDashboard from "./pages/inPatientDetail/index.jsx";
import OpdReport from "./pages/inPatientDetail/index.jsx";
import OpdDashboard from "./pages/outPatientDetail/index.jsx";
import IpdInvoice from "./pages/inPatientDetail/IpdInvoice.jsx";
import IpdCheckout from "./pages/inPatientDetail/IpdCheckout";
import AppointmentsTable from "./pages/appointments/AppointmentsTable";
import OpdReports from "./pages/inPatientDetail/OpdReports";
import IpdReports from "./pages/inPatientDetail/IpdReports";
import PatientDashboard from "./pages/patientDashboard";
import DoctorProfile from "./pages/patientDashboard/DoctorProfile";
import HomePage from "./pages/home";
import OnboardingPage from "./pages/onboarding";
import OrganizationUtilities from "./pages/admin/OrganizationUtilities";
import SubscriptionHistory from "./pages/subscription/SubscriptionHistory";
import SubscriptionManagement from "./pages/subscription/SubscriptionManagement";
import { refreshOrganizationId } from "./utils/organizationRefresh";
import { getOrganizationName, getOrganizationSlug, getOrgFromUrl, getRouteFromUrl } from "./utils/organizationUtils";
import { useLocation, useNavigate } from "react-router-dom";
import SessionTimeoutHandler from "./components/SessionTimeoutHandler";
import AppointmentAutoCancelScheduler from "./components/AppointmentAutoCancelScheduler";
import PharmacyLowStockAlertHandler from "./components/PharmacyLowStockAlertHandler";
import PathLowStockAlertHandler from "./components/PathLowStockAlertHandler";
import { isLoginValidationInProgress } from "./utils/loginSession";
import PatientLoginPage from "./pages/patientLogin";

const PatientRoutes = () => (
  <Routes>
    <Route path="/patient" element={<PatientDashboard />} />
    <Route path="/patient/doctor/:doctorId" element={<DoctorProfile />} />
    <Route path="/login" element={<Navigate to="/patient" replace />} />
    <Route path="*" element={<Navigate to="/patient" replace />} />
  </Routes>
);

const LoginRoutes = () => (
  <Routes>
    <Route path="/landing" element={<PatientLoginPage />} />
    <Route path="/patient-home" element={<Navigate to="/landing" replace />} />
    <Route path="/patient/doctor/:doctorId" element={<DoctorProfile />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/" element={<Navigate to="/landing" replace />} />
    <Route path="*" element={<Navigate to="/landing" replace />} />
  </Routes>
);

function App() {
  const [theme, colorMode] = useMode();
  const [isSidebar, setIsSidebar] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const isPatient = user?.roleName?.toLowerCase() === "patient";
  const location = useLocation();
  const navigate = useNavigate();
  const [loginValidationPending, setLoginValidationPending] = useState(
    () => isLoginValidationInProgress()
  );

  useEffect(() => {
    const syncLoginValidation = () =>
      setLoginValidationPending(isLoginValidationInProgress());
    window.addEventListener("loginValidationChanged", syncLoginValidation);
    return () =>
      window.removeEventListener("loginValidationChanged", syncLoginValidation);
  }, []);

  const showLoginRoutes = !isAuthenticated || loginValidationPending;

  // Refresh organizationId on app load if user is authenticated and organizationId is missing
  // This runs immediately when component mounts, before any API calls
  // NOTE: Skip for base users (users without organizationId by design)
  useEffect(() => {
    console.log("[App] useEffect triggered - checking for organizationId...");
    const checkAndRefreshOrgId = async () => {
      const storedUser = localStorage.getItem("user");
      console.log("[App] storedUser exists:", !!storedUser);
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          console.log("[App] Parsed user:", { id: parsedUser?.id, role: parsedUser?.roleName, orgId: parsedUser?.organizationId });
          
          // Check if user is a base user (organizationId is explicitly null, not just missing)
          // Base users should not have organizationId refreshed
          const isBaseUser = parsedUser?.organizationId === null || 
                            (parsedUser?.organizationId === undefined && !parsedUser?.hasOwnProperty('organizationId'));
          
          if (isBaseUser) {
            console.log("[App] ℹ️ Base user detected - skipping organizationId refresh");
            console.log("[App] Base user will use base tables instead of organization-specific tables.");
            return; // Don't try to refresh for base users
          }
          
          // If organizationId is missing, try to refresh it IMMEDIATELY
          if (!parsedUser?.organizationId || parsedUser.organizationId === "" || Number(parsedUser.organizationId) <= 0) {
            console.log("[App] ⚠️ organizationId missing or invalid, attempting to refresh IMMEDIATELY...");
            console.log("[App] User details:", { id: parsedUser.id, role: parsedUser.roleName, currentOrgId: parsedUser.organizationId });
            
            try {
              const orgId = await refreshOrganizationId();
              if (orgId) {
                console.log(`[App] ✅ Successfully refreshed organizationId: ${orgId}`);
                // Update the user object in state if possible
                // Reload the page to ensure all components use the updated organizationId
                console.log("[App] Reloading page to apply organizationId...");
                window.location.reload();
              } else {
                console.warn("[App] ⚠️ Could not refresh organizationId. User may be a base user without organization.");
                console.warn("[App] This user will use base tables instead of organization-specific tables.");
              }
            } catch (refreshError) {
              console.error("[App] Error during refreshOrganizationId:", refreshError);
            }
          } else {
            console.log(`[App] ✅ organizationId already set: ${parsedUser.organizationId}`);
          }
        } catch (e) {
          console.error("[App] Error checking organizationId:", e);
        }
      } else {
        console.log("[App] No user found in localStorage");
      }
    };
    
    // Run immediately, don't wait for isAuthenticated/user to be set
    checkAndRefreshOrgId();
  }, []); // Empty dependency array - run once on mount
  
  // Also run when user/isAuthenticated changes (backup)
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log("[App] User authenticated, double-checking organizationId...");
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser?.organizationId || parsedUser.organizationId === null || parsedUser.organizationId === "" || Number(parsedUser.organizationId) <= 0) {
            console.log("[App] organizationId still missing after auth, attempting refresh again...");
            refreshOrganizationId().then((orgId) => {
              if (orgId) {
                console.log(`[App] ✅ Refreshed organizationId on auth: ${orgId}`);
                window.location.reload();
              }
            });
          }
        } catch (e) {
          console.error("[App] Error in backup refresh:", e);
        }
      }
    }
  }, [isAuthenticated, user]);

  // Automatically append organization name to URL path when authenticated
  // This runs when user or organization changes, not on every route change
  // NOTE: Skip for base users (users without organizationId)
  useEffect(() => {
    if (isAuthenticated && user && !isPatient) {
      // Skip for login and onboarding routes
      if (
        location.pathname === '/login' ||
        location.pathname === '/onboarding' ||
        location.pathname === '/signup' ||
        location.pathname === '/landing' ||
        location.pathname === '/patient-home'
      ) {
        return;
      }

      // Skip for base users (users without organizationId)
      // Base users should navigate without organization prefix
      if (!user?.organizationId || user.organizationId === null) {
        // Base user - ensure URL doesn't have organization prefix
        const currentOrg = getOrgFromUrl(location.pathname);
        if (currentOrg) {
          // Remove organization prefix if it exists
          const pathWithoutOrg = location.pathname.replace(`/${currentOrg}`, '') || '/';
          const newUrl = pathWithoutOrg + (location.search || '') + (location.hash || '');
          navigate(newUrl, { replace: true });
        }
        return; // Don't process organization prefix logic for base users
      }

      const orgName = getOrganizationName(user);
      let orgSlug = orgName ? getOrganizationSlug(orgName) : null;
      const currentOrg = getOrgFromUrl(location.pathname);
      
      // If organizationName is not available but organizationId is, try to fetch it
      if (!orgName && user?.organizationId) {
        const fetchOrgName = async () => {
          try {
            const { fetchAndStoreOrganizationName } = await import("./utils/organizationUtils");
            const fetchedOrgName = await fetchAndStoreOrganizationName(user.organizationId);
            if (fetchedOrgName) {
              orgSlug = getOrganizationSlug(fetchedOrgName);
              // Update user object with organization name
              const storedUser = localStorage.getItem("user");
              if (storedUser) {
                const updatedUser = JSON.parse(storedUser);
                updatedUser.organizationName = fetchedOrgName;
                localStorage.setItem("user", JSON.stringify(updatedUser));
                // Trigger a re-render by updating the user in context
                window.dispatchEvent(new CustomEvent('userUpdated', { 
                  detail: { updatedUser } 
                }));
              }
              
              // Now update the URL with the organization prefix
              if (orgSlug) {
                const pathWithoutOrg = location.pathname.startsWith(`/${currentOrg}/`) 
                  ? location.pathname.replace(`/${currentOrg}`, '')
                  : location.pathname;
                const newPath = `/${orgSlug}${pathWithoutOrg === '/' ? '' : pathWithoutOrg}`;
                const newUrl = newPath + (location.search || '') + (location.hash || '');
                navigate(newUrl, { replace: true });
              }
            }
          } catch (error) {
            console.warn("[App] Could not fetch organization name:", error);
          }
        };
        fetchOrgName();
        return; // Exit early, will navigate after fetching
      }
      
      // Only update URL if organization in URL doesn't match current organization
      if (orgSlug && currentOrg !== orgSlug) {
        // Add organization prefix to current path
        const pathWithoutOrg = location.pathname.startsWith(`/${currentOrg}/`) 
          ? location.pathname.replace(`/${currentOrg}`, '')
          : location.pathname;
        const newPath = `/${orgSlug}${pathWithoutOrg === '/' ? '' : pathWithoutOrg}`;
        
        // Preserve query parameters and hash
        const newUrl = newPath + (location.search || '') + (location.hash || '');
        
        // Use replace to avoid adding to history
        navigate(newUrl, { replace: true });
      } else if (!orgSlug && currentOrg) {
        // If no organization, remove org prefix from URL if it exists
        const pathWithoutOrg = location.pathname.replace(`/${currentOrg}`, '') || '/';
        const newUrl = pathWithoutOrg + (location.search || '') + (location.hash || '');
        navigate(newUrl, { replace: true });
      } else if (orgSlug && !currentOrg && location.pathname === '/') {
        // If we have orgSlug but URL is just '/', add the org prefix
        const newPath = `/${orgSlug}/`;
        navigate(newPath, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, navigate, isPatient, location.pathname]); // Added location.pathname to track route changes

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SessionTimeoutHandler />
        <AppointmentAutoCancelScheduler />
        {!showLoginRoutes && !isPatient && <PharmacyLowStockAlertHandler />}
        {!showLoginRoutes && !isPatient && <PathLowStockAlertHandler />}
        <Routes>
          {/* Public routes - Home/Subscription selection (only when not authenticated) and Onboarding (always accessible) */}
          {!isAuthenticated && <Route path="/signup" element={<HomePage />} />}
          {!isAuthenticated && <Route path="/landing" element={<PatientLoginPage />} />}
          {!isAuthenticated && <Route path="/patient-home" element={<Navigate to="/landing" replace />} />}
          <Route path="/onboarding" element={<OnboardingPage />} />
          
          {/* Unauthenticated routes — default landing is patient home; staff login stays at /login */}
          {showLoginRoutes ? (
            <>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/patient/doctor/:doctorId" element={<DoctorProfile />} />
              <Route path="/" element={<Navigate to="/landing" replace />} />
              <Route path="*" element={<Navigate to="/landing" replace />} />
            </>
          ) : isPatient ? (
            /* Patient routes */
            <>
              {/* Authenticated patients can move between the landing sections and dashboard
                  without being sent through the login flow again. */}
              <Route path="/landing" element={<PatientLoginPage />} />
              <Route path="/patient" element={<PatientDashboard />} />
              <Route path="/patient/doctor/:doctorId" element={<DoctorProfile />} />
              <Route path="*" element={<Navigate to="/patient" replace />} />
            </>
          ) : (
            /* Authenticated hospital staff routes - handles both with and without org prefix */
            <>
              <Route
                path="*"
                element={
                  <Box className="app">
                    <Sidebar
                      isSidebar={isSidebar}
                      setIsCollapsed={setIsCollapsed}
                      isCollapsed={isCollapsed}
                    />
                    <Box
                      sx={{
                        marginLeft: isCollapsed ? "80px" : "216px",
                        width: isCollapsed
                          ? "calc(100% - 80px)"
                          : "calc(100% - 216px)",
                        maxWidth: isCollapsed
                          ? "calc(100% - 80px)"
                          : "calc(100% - 216px)",
                        boxSizing: "border-box",
                        overflowX: "hidden",
                        transition:
                          "margin-left 0.2s ease-in, width 0.2s ease-in, max-width 0.2s ease-in",
                      }}
                    >
                      <Navbar setIsSidebar={setIsSidebar} />
                      <Box
                        sx={{
                          px: 2,
                          pt: 1.5,
                          pb: 2,
                          boxSizing: "border-box",
                          width: "100%",
                          maxWidth: "100%",
                          overflowX: "auto",
                        }}
                      >
                      <Routes>
                      {/* Routes that work with or without organization prefix */}
                      {/* Put exact paths (without org prefix) FIRST for base users */}
                      {(user?.roleName === "doctor" || user?.roleName === "surgeon") && (
                        <>
                          <Route path="/manage-patient" element={<ManagePatient />} />
                          <Route path="/:orgSlug/manage-patient" element={<ManagePatient />} />
                          <Route path="/contacts" element={<Contacts />} />
                          <Route path="/:orgSlug/contacts" element={<Contacts />} />
                          <Route path="/form" element={<Form />} />
                          <Route path="/:orgSlug/form" element={<Form />} />
                          <Route path="/faq" element={<FAQ />} />
                          <Route path="/:orgSlug/faq" element={<FAQ />} />
                          <Route path="/calendar" element={<Calendar />} />
                          <Route path="/:orgSlug/calendar" element={<Calendar />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/:orgSlug/inventory" element={<Inventory />} />
                          <Route path="/invoice" element={<Invoices />} />
                          <Route path="/:orgSlug/invoice" element={<Invoices />} />
                          <Route path="/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/:orgSlug/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/token-dashboard" element={<TokenDashboard />} />
                          <Route path="/:orgSlug/token-dashboard" element={<TokenDashboard />} />
                          <Route path="/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/:orgSlug/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/:orgSlug/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/:orgSlug/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/:orgSlug/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/:orgSlug/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/appointments" element={<AppointmentsTable />} />
                          <Route path="/:orgSlug/appointments" element={<AppointmentsTable />} />
                        </>
                      )}
                      {isPathModuleStaffRole(user?.roleName) && (
                        <>
                          <Route path="/path-patient" element={<PathManagePatient />} />
                          <Route path="/:orgSlug/path-patient" element={<PathManagePatient />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/:orgSlug/inventory" element={<Inventory />} />
                          <Route path="/invoice" element={<Invoices />} />
                          <Route path="/:orgSlug/invoice" element={<Invoices />} />
                          <Route path="/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/:orgSlug/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/:orgSlug/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/:orgSlug/" element={<Dashboard />} />
                        </>
                      )}
                      {user?.roleName === "frontdesk" && (
                        <>
                          <Route path="/token-dashboard" element={<TokenDashboard />} />
                          <Route path="/:orgSlug/token-dashboard" element={<TokenDashboard />} />
                          <Route path="/team" element={<Team />} />
                          <Route path="/:orgSlug/team" element={<Team />} />
                          <Route path="/front-desk" element={<FrontDesk />} />
                          <Route path="/:orgSlug/front-desk" element={<FrontDesk />} />
                          <Route path="/wards-beds" element={<WardsBeds />} />
                          <Route path="/:orgSlug/wards-beds" element={<WardsBeds />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/:orgSlug/inventory" element={<Inventory />} />
                          <Route path="/invoice" element={<Invoices />} />
                          <Route path="/:orgSlug/invoice" element={<Invoices />} />
                          <Route path="/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/:orgSlug/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/:orgSlug/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/:orgSlug/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/:orgSlug/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/:orgSlug/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/:orgSlug/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/appointments" element={<AppointmentsTable />} />
                          <Route path="/:orgSlug/appointments" element={<AppointmentsTable />} />
                        </>
                      )}
                      {user?.roleName === "staff" && (
                        <>
                          <Route path="/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/:orgSlug/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/:orgSlug/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/:orgSlug/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/:orgSlug/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/:orgSlug/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/front-desk" element={<FrontDesk />} />
                          <Route path="/:orgSlug/front-desk" element={<FrontDesk />} />
                          <Route path="/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/:orgSlug/attendance-history" element={<AttendanceHistory />} />
                        </>
                      )}
                      {(user?.roleName === "admin" || user?.roleName === "superadmin" || user?.roleName === "super_admin") && (
                        <>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/:orgSlug" element={<Dashboard />} />
                          <Route path="/team" element={<Team />} />
                          <Route path="/:orgSlug/team" element={<Team />} />
                          <Route path="/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/:orgSlug/attendance-history" element={<AttendanceHistory />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/:orgSlug/inventory" element={<Inventory />} />
                          <Route path="/invoice" element={<Invoices />} />
                          <Route path="/:orgSlug/invoice" element={<Invoices />} />
                          <Route path="/front-desk" element={<FrontDesk />} />
                          <Route path="/:orgSlug/front-desk" element={<FrontDesk />} />
                          <Route path="/wards-beds" element={<WardsBeds />} />
                          <Route path="/:orgSlug/wards-beds" element={<WardsBeds />} />
                          <Route path="/organization-utilities" element={<OrganizationUtilities />} />
                          <Route path="/:orgSlug/organization-utilities" element={<OrganizationUtilities />} />
                          <Route path="/subscription/history" element={<SubscriptionHistory />} />
                          <Route path="/:orgSlug/subscription/history" element={<SubscriptionHistory />} />
                          <Route path="/subscription/management" element={<SubscriptionManagement />} />
                          <Route path="/:orgSlug/subscription/management" element={<SubscriptionManagement />} />
                          <Route path="/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/:orgSlug/ipd-dashboard/:patientId" element={<IpdDashboard />} />
                          <Route path="/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/:orgSlug/opd-dashboard/:patientId" element={<OpdDashboard />} />
                          <Route path="/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/:orgSlug/opd-report/:patientId" element={<OpdReport />} />
                          <Route path="/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/:orgSlug/ipd-invoice/:patientId" element={<IpdInvoice />} />
                          <Route path="/ipd-checkout/:patientId" element={<IpdCheckout />} />
                          <Route path="/:orgSlug/ipd-checkout/:patientId" element={<IpdCheckout />} />
                        </>
                      )}
                      {/* Dashboard routes - must be after all other routes */}
                      {(user?.roleName === "doctor" ||
                        isPathModuleStaffRole(user?.roleName)) && (
                        <>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/:orgSlug" element={<Dashboard />} />
                        </>
                      )}
                      {user?.roleName === "frontdesk" && (
                        <>
                          <Route path="/" element={<Navigate to="/token-dashboard" replace />} />
                          <Route path="/:orgSlug" element={<Navigate to="/token-dashboard" replace />} />
                        </>
                      )}
                      {user?.roleName === "pharmacy admin" && (
                        <>
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/:orgSlug" element={<Dashboard />} />
                          <Route path="/manage-patient" element={<ManagePatient />} />
                          <Route path="/:orgSlug/manage-patient" element={<ManagePatient />} />
                          <Route path="/inventory" element={<Inventory />} />
                          <Route path="/:orgSlug/inventory" element={<Inventory />} />
                          <Route path="/invoice" element={<Invoices />} />
                          <Route path="/:orgSlug/invoice" element={<Invoices />} />
                          <Route path="/prescriptions/:patientId" element={<OpdReports />} />
                          <Route path="/:orgSlug/prescriptions/:patientId" element={<OpdReports />} />
                          <Route path="/ipd-treatments/:patientId" element={<IpdReports />} />
                          <Route path="/:orgSlug/ipd-treatments/:patientId" element={<IpdReports />} />
                        </>
                      )}
                      {/* Catch-all route - redirect to dashboard for unmatched routes */}
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                      </Box>
                  </Box>
                </Box>
              }
            />
            </>
          )}
        </Routes>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
